import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Paperclip, ArrowRight, XCircle, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const ESTADOS = [
  { value: 'todos', label: 'Todos' },
  { value: 'nuevo', label: 'Nuevos' },
  { value: 'en_revision', label: 'En revisión' },
  { value: 'convertido_ot', label: 'Convertidos' },
  { value: 'descartado', label: 'Descartados' },
];

const estadoBadge: Record<string, { label: string; variant: any }> = {
  nuevo: { label: 'Nuevo', variant: 'default' },
  en_revision: { label: 'En revisión', variant: 'secondary' },
  convertido_ot: { label: 'Convertido', variant: 'outline' },
  descartado: { label: 'Descartado', variant: 'destructive' },
};

export default function BandejaAvisos() {
  const [avisos, setAvisos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todos');
  const [filtroSede, setFiltroSede] = useState('todos');
  const [filtroSistema, setFiltroSistema] = useState('todos');
  const [sedes, setSedes] = useState<any[]>([]);
  const [sistemas, setSistemas] = useState<any[]>([]);
  const [convertOpen, setConvertOpen] = useState<any | null>(null);
  const [descartarOpen, setDescartarOpen] = useState<any | null>(null);
  const [working, setWorking] = useState(false);

  const [convertForm, setConvertForm] = useState({
    titulo: '',
    descripcion: '',
    prioridad: 'media',
    fecha_limite: '',
  });
  const [motivo, setMotivo] = useState('');

  async function load() {
    setLoading(true);
    let q: any = (supabase as any)
      .from('avisos_cliente')
      .select('*, clientes(nombre), cliente_sedes(nombre), sistemas(tipo, descripcion, ubicacion, sede_id)')
      .order('created_at', { ascending: false });
    if (filtro !== 'todos') q = q.eq('estado', filtro);
    if (filtroSede !== 'todos') q = q.eq('sede_id', filtroSede);
    if (filtroSistema !== 'todos') q = q.eq('sistema_id', filtroSistema);
    const { data, error } = await q;
    if (error) toast.error(error.message || 'No se pudo cargar la bandeja de avisos');
    setAvisos(data ?? []);
    setLoading(false);
  }

  useEffect(() => { load(); }, [filtro, filtroSede, filtroSistema]);

  useEffect(() => {
    (async () => {
      const [{ data: sedesData }, { data: sistemasData }] = await Promise.all([
        (supabase as any).from('cliente_sedes').select('id, nombre').eq('activa', true).order('nombre'),
        (supabase as any).from('sistemas').select('id, tipo, descripcion, ubicacion, sede_id').order('tipo'),
      ]);
      setSedes(sedesData ?? []);
      setSistemas(sistemasData ?? []);
    })();
  }, []);

  async function marcarEnRevision(a: any) {
    await (supabase as any).from('avisos_cliente').update({ estado: 'en_revision' }).eq('id', a.id);
    load();
  }

  async function actualizarPrioridad(a: any, prioridad: string) {
    await (supabase as any).from('avisos_cliente').update({ prioridad }).eq('id', a.id);
    load();
  }

  function abrirConvertir(a: any) {
    setConvertForm({
      titulo: a.titulo,
      descripcion: a.descripcion ?? '',
      prioridad: a.prioridad || a.prioridad_sugerida || 'media',
      fecha_limite: '',
    });
    setConvertOpen(a);
  }

  async function convertir() {
    if (!convertOpen) return;
    setWorking(true);
    try {
      const { data, error } = await supabase.functions.invoke('convertir-aviso-en-ot', {
        body: {
          aviso_id: convertOpen.id,
          ...convertForm,
          fecha_limite: convertForm.fecha_limite || undefined,
        },
      });
      if (error || (data as any)?.error) throw new Error((data as any)?.error || error?.message);
      toast.success(`OT ${(data as any).numero_ot} creada`);
      setConvertOpen(null);
      load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setWorking(false);
    }
  }

  async function descartar() {
    if (!descartarOpen) return;
    if (!motivo.trim()) return toast.error('Indica el motivo');
    setWorking(true);
    try {
      await (supabase as any).from('avisos_cliente').update({
        estado: 'descartado',
        motivo_descarte: motivo.trim(),
      }).eq('id', descartarOpen.id);
      setDescartarOpen(null);
      setMotivo('');
      load();
    } finally {
      setWorking(false);
    }
  }

  async function verAdjunto(path: string) {
    const { data } = await supabase.storage.from('avisos-cliente-adjuntos').createSignedUrl(path, 60);
    if (data) window.open(data.signedUrl, '_blank');
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Bandeja de avisos del portal</h1>
        <p className="text-sm text-muted-foreground">Avisos enviados por clientes desde su portal. Conviértelos en OT o descártalos.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {ESTADOS.map(e => (
          <Button key={e.value} size="sm" variant={filtro === e.value ? 'default' : 'outline'} onClick={() => setFiltro(e.value)}>
            {e.label}
          </Button>
        ))}
      </div>

      <div className="grid gap-2 md:grid-cols-2">
        <Select value={filtroSede} onValueChange={v => { setFiltroSede(v); setFiltroSistema('todos'); }}>
          <SelectTrigger><SelectValue placeholder="Filtrar por sede" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todas las sedes</SelectItem>
            {sedes.map(s => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filtroSistema} onValueChange={setFiltroSistema}>
          <SelectTrigger><SelectValue placeholder="Filtrar por sistema" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los sistemas</SelectItem>
            {sistemas
              .filter(s => filtroSede === 'todos' || s.sede_id === filtroSede)
              .map(s => (
                <SelectItem key={s.id} value={s.id}>
                  {[s.tipo, s.descripcion, s.ubicacion].filter(Boolean).join(' · ')}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : avisos.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Sin avisos.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {avisos.map(a => {
            const est = estadoBadge[a.estado] ?? { label: a.estado, variant: 'outline' };
            const adjuntos = Array.isArray(a.adjuntos) ? a.adjuntos : [];
            return (
              <Card key={a.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <CardTitle className="text-base">{a.titulo}</CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">
                        {a.clientes?.nombre} · {format(new Date(a.created_at), "d MMM yyyy HH:mm", { locale: es })}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {a.cliente_sedes?.nombre || 'Sin sede'} · {[a.sistemas?.tipo, a.sistemas?.descripcion, a.sistemas?.ubicacion].filter(Boolean).join(' · ') || 'Sin sistema'}
                      </p>
                    </div>
                    <Badge variant={est.variant}>{est.label}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {a.descripcion && <p className="whitespace-pre-wrap">{a.descripcion}</p>}
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Prioridad cliente:</Label>
                      <Badge variant="outline" className="capitalize">{a.prioridad_sugerida}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Prioridad final:</Label>
                      <Select value={a.prioridad ?? ''} onValueChange={v => actualizarPrioridad(a, v)}>
                        <SelectTrigger className="h-7 w-32 text-xs"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="baja">Baja</SelectItem>
                          <SelectItem value="media">Media</SelectItem>
                          <SelectItem value="alta">Alta</SelectItem>
                          <SelectItem value="urgente">Urgente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {adjuntos.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {adjuntos.map((adj: any, i: number) => (
                        <Button key={i} size="sm" variant="outline" onClick={() => verAdjunto(adj.path)}>
                          <Paperclip className="h-3 w-3 mr-1" /> {adj.name || `Adjunto ${i + 1}`}
                        </Button>
                      ))}
                    </div>
                  )}
                  {a.motivo_descarte && (
                    <p className="text-xs text-muted-foreground"><strong>Motivo descarte:</strong> {a.motivo_descarte}</p>
                  )}
                  {a.orden_trabajo_id && (
                    <a href={`/asignacion-tareas`} className="text-xs text-primary inline-flex items-center gap-1">
                      <ExternalLink className="h-3 w-3" /> Ver OT generada
                    </a>
                  )}
                  {a.estado !== 'convertido_ot' && a.estado !== 'descartado' && (
                    <div className="flex gap-2 pt-2 flex-wrap">
                      {a.estado === 'nuevo' && (
                        <Button size="sm" variant="outline" onClick={() => marcarEnRevision(a)}>
                          Marcar en revisión
                        </Button>
                      )}
                      <Button size="sm" onClick={() => abrirConvertir(a)}>
                        <ArrowRight className="h-4 w-4 mr-1" /> Convertir en OT
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => setDescartarOpen(a)}>
                        <XCircle className="h-4 w-4 mr-1" /> Descartar
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Diálogo convertir */}
      <Dialog open={!!convertOpen} onOpenChange={v => !v && setConvertOpen(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Convertir aviso en OT</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título*</Label><Input value={convertForm.titulo} onChange={e => setConvertForm({ ...convertForm, titulo: e.target.value })} /></div>
            <div><Label>Descripción</Label><Textarea rows={4} value={convertForm.descripcion} onChange={e => setConvertForm({ ...convertForm, descripcion: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Prioridad</Label>
                <Select value={convertForm.prioridad} onValueChange={v => setConvertForm({ ...convertForm, prioridad: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baja">Baja</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fecha límite</Label>
                <Input type="date" value={convertForm.fecha_limite} onChange={e => setConvertForm({ ...convertForm, fecha_limite: e.target.value })} />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Se clonarán los adjuntos del aviso a la documentación de la OT.</p>
          </div>
          <DialogFooter>
            <Button onClick={convertir} disabled={working}>
              {working && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Crear OT
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo descartar */}
      <Dialog open={!!descartarOpen} onOpenChange={v => !v && setDescartarOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Descartar aviso</DialogTitle></DialogHeader>
          <div>
            <Label>Motivo*</Label>
            <Textarea rows={3} value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Indica al cliente por qué se descarta..." />
          </div>
          <DialogFooter>
            <Button variant="destructive" onClick={descartar} disabled={working}>
              {working && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Descartar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
